import { useState } from "react";
import { ResourceManager } from "../components/ResourceManager";
import { PageHeader, Pill, statusTone } from "../components/Common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Calendar } from "../components/ui/calendar";
import { useResource } from "../lib/hooks";
import { fmtDate } from "../lib/api";

export default function CalendarPage() {
  const contacts = useResource("contacts");
  const cals = useResource("calendars");
  const appts = useResource("appointments");
  const [day, setDay] = useState(new Date());
  const dayStr = day.toLocaleDateString("en-CA");
  const fields = [
    { key: "title", label: "Title", required: true },
    { key: "calendar_id", label: "Calendar", type: "select", options: cals.items.map((c) => ({ value: c.id, label: c.name })) },
    { key: "contact_id", label: "Contact", type: "select", options: contacts.items.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name || ""}` })) },
    { key: "start", label: "Start", type: "datetime-local", required: true },
    { key: "end", label: "End", type: "datetime-local" },
    { key: "location", label: "Location / Telehealth link" },
    { key: "status", label: "Status", type: "select", options: ["scheduled", "confirmed", "completed", "cancelled", "no-show"] },
    { key: "notes", label: "Notes", type: "textarea" },
  ];
  const name = (id) => { const c = contacts.items.find((x) => x.id === id); return c ? `${c.first_name} ${c.last_name || ""}` : "—"; };
  return (
    <div>
      <PageHeader overline="Scheduling" title="Calendar & Appointments" desc="Book appointments against calendars. Outlook & Google sync available in Integrations." />
      <Tabs defaultValue="appointments">
        <TabsList className="mb-6">
          <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments</TabsTrigger>
          <TabsTrigger value="day" data-testid="tab-day-view">Day view</TabsTrigger>
          <TabsTrigger value="calendars" data-testid="tab-calendars">Calendars</TabsTrigger>
        </TabsList>
        <TabsContent value="appointments">
          <ResourceManager resource="appointments" label="Appointment" fields={fields} defaults={{ status: "scheduled" }}
            columns={[{ key: "title", label: "Title" }, { key: "contact", label: "Contact", render: (r) => name(r.contact_id) },
              { key: "start", label: "Start", render: (r) => fmtDate(r.start) }, { key: "location", label: "Location" },
              { key: "status", label: "Status", render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> }]} />
        </TabsContent>
        <TabsContent value="day">
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6">
            <div className="rounded-md border bg-card p-2 w-fit"><Calendar mode="single" selected={day} onSelect={(d) => d && setDay(d)} data-testid="day-picker" /></div>
            <div className="rounded-md border bg-card p-5 space-y-2" data-testid="day-appointments">
              <div className="overline mb-2">{day.toDateString()}</div>
              {appts.items.filter((a) => (a.start || "").startsWith(dayStr)).map((a) => (
                <div key={a.id} className="border-l-2 border-primary pl-3 py-1"><div className="text-sm">{a.title}</div><div className="text-xs text-muted-foreground">{fmtDate(a.start)} · {name(a.contact_id)}</div></div>
              ))}
              {!appts.items.some((a) => (a.start || "").startsWith(dayStr)) && <div className="text-sm text-muted-foreground">No appointments.</div>}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="calendars">
          <ResourceManager resource="calendars" label="Calendar" testid="calendars"
            fields={[{ key: "name", label: "Name", required: true }, { key: "duration", label: "Default duration (mins)", type: "number" },
              { key: "owner", label: "Assigned staff" }, { key: "description", label: "Description", type: "textarea" }]}
            columns={[{ key: "name", label: "Name" }, { key: "duration", label: "Duration" }, { key: "owner", label: "Assigned" }]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
